import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  AlertTriangle,
  MessageSquare,
  Users,
  Ban,
  CheckCircle,
  Eye,
  RefreshCw,
  Loader2,
  Clock,
  XCircle,
  Unlock,
  RotateCcw,
  AlertOctagon,
  HeartHandshake,
  Phone,
  Mail,
  FileWarning,
  Frown,
  Heart
} from "lucide-react";

const ContentModerationDashboard = ({ API, token }) => {
  const [stats, setStats] = useState(null);
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [userViolations, setUserViolations] = useState([]);
  const [severeThreats, setSevereThreats] = useState([]);
  const [supportEscalations, setSupportEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showThreatModal, setShowThreatModal] = useState(false);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [threatAction, setThreatAction] = useState("reviewed");
  const [escalationAction, setEscalationAction] = useState("contacted_user");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [statsRes, messagesRes, violationsRes, threatsRes, escalationsRes] = await Promise.all([
        axios.get(`${API}/admin/content-moderation-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/flagged-messages?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/user-violations?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/severe-threats?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { threats: [], total: 0, unreviewed: 0 } })),
        axios.get(`${API}/admin/support-escalations?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { escalations: [], total: 0, unresolved: 0 } }))
      ]);

      setStats(statsRes.data);
      setFlaggedMessages(messagesRes.data.messages || []);
      setUserViolations(violationsRes.data.violations || []);
      setSevereThreats(threatsRes.data.threats || []);
      setSupportEscalations(escalationsRes.data.escalations || []);
    } catch (error) {
      console.error("Error fetching moderation data:", error);
      toast.error("Failed to load moderation data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleReviewMessage = async (action) => {
    if (!selectedMessage) return;
    
    try {
      await axios.patch(
        `${API}/admin/flagged-messages/${selectedMessage.id}/review`,
        { notes: reviewNotes, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Review recorded");
      setShowReviewModal(false);
      setSelectedMessage(null);
      setReviewNotes("");
      fetchData();
    } catch (error) {
      toast.error("Failed to record review");
    }
  };

  const handleReviewThreat = async () => {
    if (!selectedThreat) return;
    
    try {
      await axios.patch(
        `${API}/admin/severe-threats/${selectedThreat.id}/review`,
        { notes: reviewNotes, action: threatAction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Threat report reviewed");
      setShowThreatModal(false);
      setSelectedThreat(null);
      setReviewNotes("");
      setThreatAction("reviewed");
      fetchData();
    } catch (error) {
      toast.error("Failed to review threat");
    }
  };

  const handleResolveEscalation = async () => {
    if (!selectedEscalation) return;
    
    try {
      await axios.patch(
        `${API}/admin/support-escalations/${selectedEscalation.id}/resolve`,
        { notes: reviewNotes, action: escalationAction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Support escalation resolved");
      setShowEscalationModal(false);
      setSelectedEscalation(null);
      setReviewNotes("");
      setEscalationAction("contacted_user");
      fetchData();
    } catch (error) {
      toast.error("Failed to resolve escalation");
    }
  };

  const handleUnblockUser = async (userId) => {
    if (!confirm("Unblock this user from chat?")) return;
    
    try {
      await axios.post(
        `${API}/admin/user-violations/${userId}/unblock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("User unblocked");
      fetchData();
    } catch (error) {
      toast.error("Failed to unblock user");
    }
  };

  const handleResetViolations = async (userId) => {
    if (!confirm("Reset all violations for this user? This will clear their strike count.")) return;
    
    try {
      await axios.post(
        `${API}/admin/user-violations/${userId}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Violations reset");
      fetchData();
    } catch (error) {
      toast.error("Failed to reset violations");
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      LOW: "bg-yellow-100 text-yellow-700",
      MEDIUM: "bg-orange-100 text-orange-700",
      HIGH: "bg-red-100 text-red-700",
      CRITICAL: "bg-red-600 text-white"
    };
    return <Badge className={colors[severity] || "bg-gray-100"}>{severity}</Badge>;
  };

  const getSentimentBadge = (sentiment) => {
    if (!sentiment) return null;
    const sentimentType = sentiment.sentiment || sentiment;
    const colors = {
      distressed: "bg-red-100 text-red-700",
      frustrated: "bg-orange-100 text-orange-700",
      anxious: "bg-yellow-100 text-yellow-700",
      negative: "bg-gray-100 text-gray-700",
      neutral: "bg-blue-100 text-blue-700",
      positive: "bg-green-100 text-green-700"
    };
    return <Badge className={colors[sentimentType] || "bg-gray-100"}>{sentimentType}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-moderation-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Content Moderation
          </h2>
          <p className="text-gray-500">Monitor and manage flagged content, threats, and user support</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => fetchData(true)}
          disabled={refreshing}
          data-testid="refresh-moderation-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-2 border-red-100">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Flagged</p>
                  <p className="text-2xl font-bold text-red-600">{stats.total_flagged_messages}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-100">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Unreviewed</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.unreviewed_messages}</p>
                </div>
                <Eye className="h-8 w-8 text-orange-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-red-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Severe Threats</p>
                  <p className="text-2xl font-bold text-red-700">{stats.severe_threats?.total || 0}</p>
                  {stats.severe_threats?.unreviewed > 0 && (
                    <p className="text-xs text-red-500">{stats.severe_threats.unreviewed} pending</p>
                  )}
                </div>
                <AlertOctagon className="h-8 w-8 text-red-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-100">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Support Alerts</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.support_escalations?.total || 0}</p>
                  {stats.support_escalations?.unresolved > 0 && (
                    <p className="text-xs text-purple-500">{stats.support_escalations.unresolved} pending</p>
                  )}
                </div>
                <HeartHandshake className="h-8 w-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-100">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Users w/ Violations</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.total_users_with_violations}</p>
                </div>
                <Users className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-100">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Blocked Users</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.currently_blocked_users}</p>
                </div>
                <Ban className="h-8 w-8 text-gray-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="messages">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="messages" className="flex items-center gap-1" data-testid="tab-flagged-messages">
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Flagged Messages</span>
            <span className="sm:hidden">Flagged</span>
            {stats?.unreviewed_messages > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-xs">{stats.unreviewed_messages}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="threats" className="flex items-center gap-1" data-testid="tab-severe-threats">
            <AlertOctagon className="w-4 h-4" />
            <span className="hidden sm:inline">Severe Threats</span>
            <span className="sm:hidden">Threats</span>
            {stats?.severe_threats?.unreviewed > 0 && (
              <Badge className="ml-1 bg-red-600 text-white text-xs animate-pulse">{stats.severe_threats.unreviewed}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="support" className="flex items-center gap-1" data-testid="tab-support-escalations">
            <HeartHandshake className="w-4 h-4" />
            <span className="hidden sm:inline">Support Escalations</span>
            <span className="sm:hidden">Support</span>
            {stats?.support_escalations?.unresolved > 0 && (
              <Badge className="ml-1 bg-purple-500 text-white text-xs">{stats.support_escalations.unresolved}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-1" data-testid="tab-user-violations">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">User Violations</span>
            <span className="sm:hidden">Users</span>
          </TabsTrigger>
        </TabsList>

        {/* Flagged Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Flagged Messages
              </CardTitle>
              <CardDescription>Review messages flagged by the AI moderation system</CardDescription>
            </CardHeader>
            <CardContent>
              {flaggedMessages.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500">No flagged messages. All clear!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Message (Censored)</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Violations</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flaggedMessages.map((msg) => (
                        <TableRow key={msg.id} className={!msg.reviewed ? "bg-red-50" : ""}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{msg.user_name}</p>
                              <p className="text-xs text-gray-500">{msg.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <p className="truncate text-sm" title={msg.censored_message}>
                              {msg.censored_message?.substring(0, 100)}...
                            </p>
                          </TableCell>
                          <TableCell>{getSeverityBadge(msg.moderation_result?.severity)}</TableCell>
                          <TableCell>
                            <Badge variant="outline">#{msg.violation_number}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={msg.action_taken === "blocked" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}>
                              {msg.action_taken}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {formatDate(msg.created_at)}
                          </TableCell>
                          <TableCell>
                            {msg.reviewed ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Reviewed
                              </Badge>
                            ) : (
                              <Badge className="bg-orange-100 text-orange-700">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {!msg.reviewed && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedMessage(msg);
                                  setShowReviewModal(true);
                                }}
                                data-testid={`review-message-${msg.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Review
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Severe Threats Tab */}
        <TabsContent value="threats">
          <Card className="border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertOctagon className="h-5 w-5" />
                Severe Threats
              </CardTitle>
              <CardDescription>Critical threats requiring immediate attention - potential violence, self-harm, or illegal activity</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {severeThreats.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500">No severe threats detected</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Threat Category</TableHead>
                        <TableHead>Message Preview</TableHead>
                        <TableHead>Detected Keywords</TableHead>
                        <TableHead>Reported At</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {severeThreats.map((threat) => (
                        <TableRow key={threat.id} className={!threat.reviewed_by_admin ? "bg-red-50" : ""}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{threat.user_name || "Unknown"}</p>
                              <p className="text-xs text-gray-500">{threat.user_email || threat.user_id}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-red-600 text-white">
                              {threat.category || "threat"}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <p className="truncate text-sm text-red-600" title={threat.message}>
                              {threat.message?.substring(0, 80)}...
                            </p>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {threat.detected_threats?.slice(0, 3).map((word, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs text-red-600 border-red-300">
                                  {word}
                                </Badge>
                              ))}
                              {threat.detected_threats?.length > 3 && (
                                <Badge variant="outline" className="text-xs">+{threat.detected_threats.length - 3}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {formatDate(threat.created_at || threat.reported_at)}
                          </TableCell>
                          <TableCell>
                            {threat.reviewed_by_admin ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Reviewed
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700 animate-pulse">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Urgent
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {!threat.reviewed_by_admin && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => {
                                  setSelectedThreat(threat);
                                  setShowThreatModal(true);
                                }}
                                data-testid={`review-threat-${threat.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Review Now
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Escalations Tab */}
        <TabsContent value="support">
          <Card className="border-purple-200">
            <CardHeader className="bg-purple-50">
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <HeartHandshake className="h-5 w-5" />
                Support Escalations
              </CardTitle>
              <CardDescription>Users who may need support based on sentiment analysis - distress, frustration, or anxiety detected</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {supportEscalations.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="h-12 w-12 text-purple-300 mx-auto mb-4" />
                  <p className="text-gray-500">No support escalations at this time</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Sentiment</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Detected At</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supportEscalations.map((escalation) => (
                        <TableRow key={escalation.id} className={!escalation.resolved ? "bg-purple-50" : ""}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{escalation.user_name || "Unknown"}</p>
                              <p className="text-xs text-gray-500">{escalation.user_email || escalation.user_id}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getSentimentBadge(escalation.sentiment)}
                            {escalation.sentiment?.confidence && (
                              <p className="text-xs text-gray-400 mt-1">
                                {Math.round(escalation.sentiment.confidence * 100)}% confidence
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <p className="truncate text-sm" title={escalation.message}>
                              {escalation.message?.substring(0, 80)}...
                            </p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-gray-600">{escalation.reason}</p>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {formatDate(escalation.created_at)}
                          </TableCell>
                          <TableCell>
                            {escalation.resolved ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Resolved
                              </Badge>
                            ) : (
                              <Badge className="bg-purple-100 text-purple-700">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {!escalation.resolved && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                onClick={() => {
                                  setSelectedEscalation(escalation);
                                  setShowEscalationModal(true);
                                }}
                                data-testid={`resolve-escalation-${escalation.id}`}
                              >
                                <HeartHandshake className="w-3 h-3 mr-1" />
                                Resolve
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Violations Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                User Violations
              </CardTitle>
              <CardDescription>Manage users with chat violations and blocks</CardDescription>
            </CardHeader>
            <CardContent>
              {userViolations.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500">No user violations recorded</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Violation Count</TableHead>
                        <TableHead>Last Violation</TableHead>
                        <TableHead>Block Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userViolations.map((v) => {
                        const isBlocked = v.blocked_until && new Date(v.blocked_until) > new Date();
                        return (
                          <TableRow key={v.user_id} className={isBlocked ? "bg-red-50" : ""}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{v.user_name || "Unknown"}</p>
                                <p className="text-xs text-gray-500">{v.user_email || v.user_id}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={v.violation_count >= 3 ? "border-red-500 text-red-500" : ""}>
                                {v.violation_count} strikes
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {formatDate(v.last_violation_at)}
                            </TableCell>
                            <TableCell>
                              {isBlocked ? (
                                <Badge className="bg-red-100 text-red-700">
                                  <Ban className="w-3 h-3 mr-1" />
                                  Blocked until {formatDate(v.blocked_until)}
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Active
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {isBlocked && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleUnblockUser(v.user_id)}
                                    data-testid={`unblock-user-${v.user_id}`}
                                  >
                                    <Unlock className="w-3 h-3 mr-1" />
                                    Unblock
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleResetViolations(v.user_id)}
                                  title="Reset violation count"
                                  data-testid={`reset-violations-${v.user_id}`}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Flagged Message Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Flagged Message</DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p className="font-medium">{selectedMessage.user_name}</p>
                  <p className="text-sm text-gray-500">{selectedMessage.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Violation #</p>
                  <p className="font-medium">{selectedMessage.violation_number}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Original Message</p>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm">{selectedMessage.censored_message}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Detected Issues</p>
                <div className="flex flex-wrap gap-2">
                  {selectedMessage.moderation_result?.violations?.map((v, idx) => (
                    <Badge key={idx} variant="outline" className="text-red-600">
                      {v.type}: {v.detected_words?.join(", ")}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Admin Notes</p>
                <Textarea
                  placeholder="Add notes about this review..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  data-testid="review-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="default"
              onClick={() => handleReviewMessage("acknowledged")}
              data-testid="confirm-review-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Severe Threat Modal */}
      <Dialog open={showThreatModal} onOpenChange={setShowThreatModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertOctagon className="h-5 w-5" />
              Review Severe Threat
            </DialogTitle>
          </DialogHeader>
          
          {selectedThreat && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 font-medium mb-2">This is a critical alert that may require immediate action</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p className="font-medium">{selectedThreat.user_name || "Unknown"}</p>
                  <p className="text-sm text-gray-500">{selectedThreat.user_email || selectedThreat.user_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <Badge className="bg-red-600 text-white">{selectedThreat.category || "threat"}</Badge>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Full Message</p>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">{selectedThreat.message}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Detected Keywords</p>
                <div className="flex flex-wrap gap-2">
                  {selectedThreat.detected_threats?.map((word, idx) => (
                    <Badge key={idx} variant="outline" className="text-red-600 border-red-300">
                      {word}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Action Taken</p>
                <Select value={threatAction} onValueChange={setThreatAction}>
                  <SelectTrigger data-testid="threat-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reviewed">Reviewed - No further action</SelectItem>
                    <SelectItem value="user_warned">User warned</SelectItem>
                    <SelectItem value="user_blocked">User blocked</SelectItem>
                    <SelectItem value="authorities_notified">Authorities notified</SelectItem>
                    <SelectItem value="escalated">Escalated to management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Admin Notes</p>
                <Textarea
                  placeholder="Document the actions taken and any follow-up required..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  data-testid="threat-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowThreatModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReviewThreat}
              data-testid="confirm-threat-review-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Support Escalation Modal */}
      <Dialog open={showEscalationModal} onOpenChange={setShowEscalationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <HeartHandshake className="h-5 w-5" />
              Resolve Support Escalation
            </DialogTitle>
          </DialogHeader>
          
          {selectedEscalation && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-600 font-medium">This user may need support - please handle with care</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p className="font-medium">{selectedEscalation.user_name || "Unknown"}</p>
                  <p className="text-sm text-gray-500">{selectedEscalation.user_email || selectedEscalation.user_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Detected Sentiment</p>
                  {getSentimentBadge(selectedEscalation.sentiment)}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">User's Message</p>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm">{selectedEscalation.message}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Reason for Escalation</p>
                <p className="text-sm text-gray-600">{selectedEscalation.reason}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Action Taken</p>
                <Select value={escalationAction} onValueChange={setEscalationAction}>
                  <SelectTrigger data-testid="escalation-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contacted_user">Contacted user via email</SelectItem>
                    <SelectItem value="phone_call">Made phone call</SelectItem>
                    <SelectItem value="in_app_message">Sent in-app message</SelectItem>
                    <SelectItem value="referred_support">Referred to support team</SelectItem>
                    <SelectItem value="no_action_needed">No action needed (false positive)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Resolution Notes</p>
                <Textarea
                  placeholder="Document how the user was helped or why no action was needed..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  data-testid="escalation-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalationModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleResolveEscalation}
              data-testid="confirm-escalation-resolve-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentModerationDashboard;
